import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MainLayout from "../components/MainLayout";
import TenderView from "../components/TenderView";
import CheckoutItems from "../components/CheckoutItem";
import CheckoutSummary from "../components/CheckoutSummary";
import Promotion from "../components/Promotion";
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import { getSessionId } from '../utils/sessionId';
import { toast } from 'react-toastify';
import useNavigationGuard from '../hooks/useNavigationGuard';
import GuardedNavigationModal from '../components/modals/GuardedNavigationModal';
import EftposLinklyModal from "../components/modals/EftposLinklyModal";
import BillingPrintModal from "../components/modals/BillingPrintModal";
import DigitalReceiptModal from "../components/modals/DigitalReceiptModal";
import StripePaymentLinkPanel from "../components/StripePaymentLinkPanel";
import { useConnection } from "../offline/connection";
import { canDo } from "../offline/state";
import useCustomerDisplayPayment from "../hooks/useCustomerDisplayPayment";
import { isCustomerDisplayAvailable } from "../utils/customerDisplayBridge";


const PaymentView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Offline, only what the offline engine says it can do is offered (a no-op online / with no engine).
  const { state: offlineState } = useConnection();
  const [isPrepaid, setIsPrepaid] = useState(location.state?.is_prepaid || false);
  const [orderTypeNum, setOrderTypeNum] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [tableId, setTableId] = useState(null);
  const [resumingUnpaidOrder, setResumingUnpaidOrder] = useState(false);
  // False until the order / table id in the address (or the saved order) has been read. The sections
  // below load their own data from it, so they wait for this instead of asking first with no id and
  // then again with it.
  const [idsReady, setIdsReady] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState(null);
  const [selectedTender, setSelectedTender] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const [selectedCartItems, setSelectedCartItems] = useState([]);
  //const { showModal, confirmNavigation, cancelNavigation, guardedNavigate } = useNavigationGuard(orderTypeNum == 1 || orderTypeNum == 2);
  const shouldBlock = (orderTypeNum == 1 || orderTypeNum == 2) || isPrepaid;
  const { showModal, confirmNavigation, cancelNavigation, guardedNavigate } = useNavigationGuard(shouldBlock);
  const [refreshKey, setRefreshKey] = useState(0);
  const [splitPaymentActive, setSplitPaymentActive] = useState(false);

  const [uuid, setUuid] = useState(null);
  const [amount, setAmount] = useState(0);
  const [splitType, setSplitType] = useState('item');
  const [splitCount, setSplitCount] = useState(0);
  const [cashAmount, setCashAmount] = useState('');
  // Sticky flag: once "Cash + EFTPOS" is chosen, this stays true even after the first payment
  // lands and splitType syncs back from the backend as plain 'equal' (it has no concept of
  // 'cash_eftpos' - see CheckoutSummary.js) - CheckoutSummary needs this to keep labeling the
  // two splits "Cash"/"EFTPOS" and auto-selecting the matching tender for the second payment.
  const [cashEftposMode, setCashEftposMode] = useState(false);
  const [freezeSplitControls, setFreezeSplitControls] = useState(false);
  const [showEftModal, setShowEftModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingPrintEnabled, setBillingPrintEnabled] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [digitalReceiptEnabled, setDigitalReceiptEnabled] = useState(false);
  const [receiptSummary, setReceiptSummary] = useState(null);

  // C20 Pro customer screen: what this payment screen shows (items + totals) is relayed to the
  // second screen. Only wired when the wrapper has the bridge, so nothing changes without it.
  const customerDisplayOn = isCustomerDisplayAvailable();
  const [displayItems, setDisplayItems] = useState(null);
  const [displayTotals, setDisplayTotals] = useState(null);
  const [paymentDone, setPaymentDone] = useState(false);
  useCustomerDisplayPayment({ items: displayItems, totals: displayTotals, enabled: !paymentDone });

  // Shared by both payment paths below (CheckoutSummary's own cash/card flow and the separate
  // EftposLinklyModal terminal flow) so "what happens right after a payment completes" only has
  // one definition: show the digital receipt first if that POS setting is on, otherwise fall
  // straight through to the existing billing-print-prompt-or-dashboard behavior.
  const proceedAfterPayment = (billingEnabled) => {
    setPaymentDone(true);
    if (digitalReceiptEnabled) {
      setShowReceiptModal(true);
    } else if (billingEnabled && canDo(offlineState, 'printing')) {
      setShowBillingModal(true);
    } else {
      navigate('/dashboard');
    }
  };

  const handleReceiptDone = () => {
    setShowReceiptModal(false);
    billingPrintEnabled && canDo(offlineState, 'printing') ? setShowBillingModal(true) : navigate('/dashboard');
  };
  const [orderName, setOrderName] = useState("");
  const [eftError, setEftError] = useState(null);

  const PAYMENT_MODES = {
    cod: 1,
    cash: 1,
    eftpos: 2,
    online: 3,
    outofsystem: 4
  };

  // const handleConfirm = () => {
  //   localStorage.removeItem('orderType');
  //   localStorage.removeItem('selectedTables');
  //   confirmNavigation();
  // };

  const handleConfirm = async () => {
    // Backend decides whether there's actually anything prepaid/unpaid to
    // discard for this order - don't gate the call on frontend isPrepaid
    // state, which may not have loaded yet if navigation happens quickly.
    if (orderId) {
      try {
        await callAPI(
          apiUrls.EMPTY_OCCUPANCY,
          {},
          'POST',
          {
            order_id: orderId,
            table_id: tableId,
          }
        );
      } catch (err) {
        console.error('Failed to clear table occupancy', err);
      }
    }

    localStorage.removeItem('orderType');
    localStorage.removeItem('selectedTables');

    confirmNavigation();
  };

  const triggerRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    if (!splitPaymentActive) {
      setSplitType('item');
      // Keep the array that is already there when it is empty: a fresh [] is a new value to React, and the
      // checkout summary is reloaded whenever the selection changes - so on opening this page it was asked twice.
      setSelectedCartItems((prev) => (prev.length ? [] : prev));
      setCashAmount('');
      setCashEftposMode(false);
    }
  }, [splitPaymentActive]);

  useEffect(() => {
    if (splitType === 'cash_eftpos') {
      setCashEftposMode(true);
    }
  }, [splitType]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/');
      return;
    }

    const query = new URLSearchParams(window.location.search);
    const table_id = query.get('table_id');
    const order_id = query.get('order_id') || localStorage.getItem('order_id');

    setTableId(table_id);
    setOrderId(order_id);
    // Only ever present when this screen was reached via the Orders page's "Checkout" icon on an
    // already-unpaid order (see Orders.js handleCheckout) — never set on a fresh order's first
    // payment, unlike order_id above which localStorage can populate either way.
    setResumingUnpaidOrder(query.get('resuming_unpaid_order') === '1');
    setIdsReady(true);

    const fetchOrderType = async () => {
      try {
        const session_id = getSessionId();
        const res = await callAPI(
          `${apiUrls.GET_ORDER_TYPE_NUM}?session_id=${session_id}&order_id=${order_id}&table_id=${table_id || ''}`,
          {},
          'GET'
        );
        const data = res?.data?.data || res?.data || {};
        setOrderTypeNum(Number(data.orderType));
        // The same order id sometimes comes back as a number where the address had a string: keep the
        // value that is already there, so nothing keyed on the order id reloads for no reason.
        setOrderId((prev) => (data.forderId != null && String(data.forderId) === String(prev) ? prev : data.forderId));
        setIsPrepaid(!!data.is_prepaid);
      } catch (err) {
        console.error(err);
      }
    };

    fetchOrderType();
  }, [navigate]);




  useEffect(() => {
    const handler = (e) => {
      const { status, message } = e.detail || {};

      if (status == 'success') {
        toast.success(message || 'EFTPOS payment successful');
        setUuid(null)
      }

      if (status == 'failed') {
        toast.error(message || 'EFTPOS payment failed');
        setShowEftModal(false);
        setUuid(null);
      }
    };

    window.addEventListener('eftpos-payment-result', handler);
    return () => window.removeEventListener('eftpos-payment-result', handler);
  }, []);

  useEffect(() => {
    const handleEftposLinkly = (e) => {
      const detail = e.detail || {};

      setShowEftModal(true);
      console.log("dddddd", detail)
      if (detail?.error) {
        setEftError(detail);
        setUuid(null);
        return;
      }

      setEftError(null);

      if ('uuid' in detail) setUuid(detail.uuid ?? null);
      if ('amount' in detail) setAmount(detail.amount ?? 0);
    };

    window.addEventListener('eftpos-payment-linkly', handleEftposLinkly);
    return () => window.removeEventListener('eftpos-payment-linkly', handleEftposLinkly);
  }, []);

  // Don't block the whole page behind one loader waiting on order-type to
  // resolve - render immediately and let each section (Promotion, cart
  // items, checkout summary, tender view) show/fetch progressively as its
  // own data arrives. All of them already handle orderTypeNum/paymentMethod
  // being null on first render.

  return (

    <MainLayout guardedNavigate={guardedNavigate}>
      <div className="pageContent paymentViewPage">
        <div className="container">
          <h2 className="lgTitle mb-4 text-center">
            Choose a Payment Method
          </h2>

          <div className="row g-3 paymentRow">

            <TenderView
              PAYMENT_MODES={PAYMENT_MODES}
              orderTypeNum={orderTypeNum}
              orderId={orderId}
              onPaymentModeChange={(method) => {
                setPaymentMethod(method);
                setSelectedTender(null);
              }}
              onTenderSelect={({ paymentMethod, selectedTender }) => {
                setPaymentMethod(paymentMethod);
                setSelectedTender(selectedTender);
              }}
            />

            <div className="col-lg-auto pCenterCol">

              {canDo(offlineState, 'payLink') && (
              <StripePaymentLinkPanel
                orderId={orderId}
                orderTypeNum={orderTypeNum}
                paymentMethod={paymentMethod}
                PAYMENT_MODES={PAYMENT_MODES}
              />
              )}

              {canDo(offlineState, 'discounts') && (
              <Promotion
                ready={idsReady}
                orderId={orderId}
                tableId={tableId}
                paymentMethod={paymentMethod}
                selectedOfferId={selectedOffer?.id}
                onOfferSelect={setSelectedOffer}
              />
              )}


              <CheckoutItems
                key={`items-${refreshKey}`}
                ready={idsReady}
                orderTypeNum={orderTypeNum}
                orderId={orderId}
                tableId={tableId}
                onCartSelectionChange={setSelectedCartItems}
                splitPaymentActive={splitPaymentActive}
                onSplitPaymentChange={setSplitPaymentActive}
                splitType={splitType}
                onSplitTypeChange={setSplitType}
                splitCount={splitCount}
                onSplitCountChange={setSplitCount}
                cashAmount={cashAmount}
                onCashAmountChange={setCashAmount}
                freezeSplitControls={freezeSplitControls}
                onOrderNameChange={setOrderName}
                onDisplayItemsChange={customerDisplayOn ? setDisplayItems : null}
              />
            </div>

            <CheckoutSummary
              PAYMENT_MODES={PAYMENT_MODES}
              orderTypeNum={orderTypeNum}
              orderId={orderId}
              tableId={tableId}
              resumingUnpaidOrder={resumingUnpaidOrder}
              selectedOffer={selectedOffer}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              selectedTender={selectedTender}
              selectedCartItems={selectedCartItems}
              onPaymentSuccess={triggerRefresh}
              onBillingConfigChange={setBillingPrintEnabled}
              onDigitalReceiptConfigChange={setDigitalReceiptEnabled}
              onSummaryReady={setReceiptSummary}
              onPaymentComplete={proceedAfterPayment}
              splitPaymentActive={splitPaymentActive}
              setUuid={setUuid}
              setAmount={setAmount}
              onEftposRequest={({ uuid: newUuid, amount: newAmount }) => {
                setUuid(newUuid);
                setAmount(newAmount);
              }}
              splitType={splitType}
              splitCount={splitCount}
              cashAmount={cashAmount}
              cashEftposMode={cashEftposMode}

              setSplitPaymentActive={setSplitPaymentActive}
              setSplitType={setSplitType}
              setSplitCount={setSplitCount}
              setFreezeSplitControls={setFreezeSplitControls}
              isPrepaid={isPrepaid}
              orderName={orderName}
              onDisplayTotalsChange={customerDisplayOn ? setDisplayTotals : null}
            />
          </div>
        </div>
      </div>
      <EftposLinklyModal
        isOpen={showEftModal}
        amount={amount}
        uuid={uuid}
        error={eftError}
        isPrepaid={isPrepaid}
        onClose={() => {
          setShowEftModal(false);
          setUuid(null);
          setEftError(null);
        }}
        onPaymentComplete={() => {
          setShowEftModal(false);
          setUuid(null);
          setEftError(null);
          proceedAfterPayment(billingPrintEnabled);
        }}
      />
      <BillingPrintModal
        isOpen={showBillingModal}
        orderId={orderId}
        tableId={tableId}
        paymentMethod={paymentMethod}
      />
      <DigitalReceiptModal
        isOpen={showReceiptModal}
        orderId={orderId}
        tableId={tableId}
        paymentMethod={paymentMethod}
        summary={receiptSummary}
        tableLabel={receiptSummary?.table?.is_pager ? 'Pager' : 'Table'}
        onClose={handleReceiptDone}
      />
      <GuardedNavigationModal
        isOpen={showModal}
        onCancel={cancelNavigation}
        onConfirm={handleConfirm}
      />
    </MainLayout>
  );
};

export default PaymentView;
